/* Bookings API client — Phase 5 Milestone 1
 *
 * Wraps the NestJS /v1/bookings endpoints. Keeps the SAME UI shape the
 * admin already uses (month-keyed `bookings` map, string money fields,
 * "Agreement Done" with a space). Adapter functions handle the small
 * differences between API enum and UI label.
 */
import { api } from "./api";

const BASE = "/bookings";

// ─── Adapters ──────────────────────────────────────────────────────

/** API → UI: enum underscore → space label, ISO date → YYYY-MM-DD */
export function bookingApiToUi(b) {
  if (!b) return b;
  return {
    ...b,
    dealStatus: typeof b.dealStatus === "string" ? b.dealStatus.replace("_", " ") : b.dealStatus,
    bookingDate: b.bookingDate ? String(b.bookingDate).slice(0, 10) : "",
    paymentDate: b.paymentDate ? String(b.paymentDate).slice(0, 10) : "",
    // Same treatment as paymentDate: the API returns a full ISO string
    // and the form fields are date inputs expecting YYYY-MM-DD.
    billSubmittedAt: b.billSubmittedAt ? String(b.billSubmittedAt).slice(0, 10) : "",
    /* Incentive Sheets reads these as typeMeta.dateKey. The modal has
       always asked for the date; until the columns existed it was written
       to local state and dropped on save, so every paid row showed a dash
       after reload. */
    builderPaidDate: b.builderPaidDate ? String(b.builderPaidDate).slice(0, 10) : "",
    skyDreamPaidDate: b.skyDreamPaidDate ? String(b.skyDreamPaidDate).slice(0, 10) : "",
    cancelledAt: b.cancelledAt ? String(b.cancelledAt) : null,
    // Decimals come back as strings from Prisma — keep them as strings to
    // match the form inputs (the UI uses `inr()` to format on render).
    // Ledger badge + filters read `executive`; the API stores executiveName.
    executive: b.executiveName || b.executive || "",
    // Gallery items: { id, url, name, mimeType, caption, sortOrder }.
    // The uploader/AttachmentPill read `dataUrl` + `type`; base64 files are
    // stored in `url`, so surface them under those keys for display.
    gallery: Array.isArray(b.gallery)
      ? b.gallery.map((g) => ({
          ...g,
          dataUrl: g.dataUrl || g.url,
          type: g.type || g.mimeType,
        }))
      : [],
  };
}

/** UI → API: enum label "Agreement Done" → "Agreement_Done", strip date noise */
export function bookingUiToApi(form) {
  const out = { ...form };
  if (out.dealStatus && typeof out.dealStatus === "string") {
    out.dealStatus = out.dealStatus.replace(" ", "_");
  }
  // Empty strings → omit so the backend uses defaults / leaves as null
  for (const k of ["paymentDate", "billSubmittedAt", "builderPaidDate", "skyDreamPaidDate", "paymentMode", "billDocumentUrl", "billDocumentKey",
                   "executiveId", "executiveName", "config", "flatNo", "firmBillStatus", "note", "srNo"]) {
    if (out[k] === "") delete out[k];
  }
  /* srNo is now editable, so it must reach the API. It used to be stripped
     here as "a UI-only field the backend doesn't know about", which was true
     until UpdateBookingDto gained it.

     Three separate layers independently defended it -- this adapter, the
     destructure in AppDataContext.updateBooking, and the DTO's whitelist.
     Each made sense while the number was server-assigned by nextSrNo();
     together they meant an edit saved with a 200 and changed nothing.

     An empty value is dropped by the loop above rather than sent: on create
     the field is "", and @IsInt would reject that and fail the whole
     request. Omitted means nextSrNo() assigns it as before. */
  // Money fields — keep as strings; backend DTO @Transform coerces.
  // Strip UI-only fields the backend doesn't know about.
  delete out.id;
  delete out.createdAt;
  delete out.updatedAt;
  delete out.createdById;
  delete out.createdBy;
  delete out.executive;
  return out;
}

/** "April 2026" → "2026-04" */
const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
export function monthLabelToYM(label) {
  if (!label) return "";
  const [monthName, year] = label.split(" ");
  const m = MONTH_NAMES.indexOf(monthName);
  if (m < 0 || !year) return "";
  return `${year}-${String(m + 1).padStart(2, "0")}`;
}

/** "2026-04" → "April 2026" */
export function ymToMonthLabel(ym) {
  if (!ym) return "";
  const [year, mm] = ym.split("-");
  const idx = parseInt(mm, 10) - 1;
  if (idx < 0 || idx > 11) return "";
  return `${MONTH_NAMES[idx]} ${year}`;
}

// ─── API methods ───────────────────────────────────────────────────

export const bookingsApi = {
  async listByMonth(monthLabel) {
    const month = monthLabelToYM(monthLabel);
    if (!month) return { items: [], total: 0 };
    const res = await api.get(`${BASE}?month=${encodeURIComponent(month)}&scope=all&pageSize=500`);
    return {
      items: (res.items || []).map(bookingApiToUi),
      total: res.total || 0,
    };
  },

  /* Date-range fetch, used by the Bookings Ledger's financial-year view.
     GET /v1/bookings already supports from/to (bookings.dto.ts:279-283,
     applied in bookings.service.ts:74-76) and the comment there notes the
     range overrides `month`, so no API change was needed. Dates are ISO
     "YYYY-MM-DD" -- @IsDateString rejects anything else. */
  async listByRange(from, to) {
    const res = await api.get(
      `${BASE}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&scope=all&pageSize=500`,
    );
    return {
      items: (res.items || []).map(bookingApiToUi),
      total: res.total || 0,
    };
  },

  async months() {
    // Returns array of "YYYY-MM" strings, convert to "Month YYYY" labels.
    const ymList = await api.get(`${BASE}/months`);
    return (ymList || []).map(ymToMonthLabel).filter(Boolean);
  },

  async getOne(id) {
    const res = await api.get(`${BASE}/${encodeURIComponent(id)}`);
    return bookingApiToUi(res);
  },

  async create(uiForm) {
    const res = await api.post(BASE, bookingUiToApi(uiForm));
    return bookingApiToUi(res);
  },

  async update(id, patch) {
    const res = await api.patch(`${BASE}/${encodeURIComponent(id)}`, bookingUiToApi(patch));
    return bookingApiToUi(res);
  },

  async cancel(id, reason) {
    const res = await api.post(`${BASE}/${encodeURIComponent(id)}/cancel`, { reason: reason || "Cancelled" });
    return bookingApiToUi(res);
  },

  async uncancel(id) {
    const res = await api.post(`${BASE}/${encodeURIComponent(id)}/uncancel`, {});
    return bookingApiToUi(res);
  },

  async delete(id) {
    return api.delete(`${BASE}/${encodeURIComponent(id)}`);
  },
};
